import React from 'react';
import { FiDollarSign, FiCheckCircle, FiClock, FiShield, FiZap, FiBarChart2, FiUsers } from 'react-icons/fi';

const CloudSavings = () => {
  const costAreas = [
    {
      icon: <FiDollarSign className="w-6 h-6" />,
      title: 'No Server Hardware Costs',
      description: 'On-premise systems require expensive servers, storage devices, and networking equipment. These need to be purchased, maintained, and replaced every 3-5 years. Cloud ERP eliminates this capital expenditure completely.',
      stat: '₹2-4 Lakhs saved annually on hardware costs',
      solution: 'Truyon runs entirely in the cloud—no servers, no hardware, no capital expenses.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'No IT Staff Overhead',
      description: 'On-premise ERPs require dedicated IT staff to manage servers, perform backups, apply updates, and troubleshoot issues. Cloud ERPs eliminate most of these staffing requirements.',
      stat: '₹1-3 Lakhs saved annually on IT staff costs',
      solution: 'Truyon is fully managed—no IT staff needed to maintain the system.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'No Software License Maintenance',
      description: 'Beyond the initial license cost, on-premise ERPs require annual maintenance fees, upgrade costs, and per-user licensing. These costs add up significantly over time.',
      stat: '₹1-2 Lakhs saved annually on license maintenance',
      solution: 'Truyon uses a simple, affordable subscription—no hidden license maintenance fees.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'No Backup and Disaster Recovery Costs',
      description: 'On-premise systems need backup hardware, off-site storage, and disaster recovery planning. These are expensive and complex to implement properly. Cloud providers handle this automatically.',
      stat: '₹50,000-1 Lakh saved annually on backup and recovery',
      solution: 'Truyon includes automatic backup and disaster recovery—no extra cost.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'No Energy and Infrastructure Costs',
      description: 'Servers consume significant electricity and require cooling, UPS systems, and secure server rooms. These operational costs are often overlooked but add up substantially.',
      stat: '₹50,000-1 Lakh saved annually on energy and infrastructure',
      solution: 'Truyon runs on optimized cloud infrastructure—no energy costs for your institution.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'No Training and Implementation Overhead',
      description: 'On-premise implementations require extensive training, complex migrations, and dedicated project management. Cloud ERPs are simpler to implement and easier to learn.',
      stat: '₹1-2 Lakhs saved annually on training and implementation',
      solution: 'Truyon is intuitive and easy to implement—minimal training required.',
      color: 'bg-blue-50'
    }
  ];

  const savingsBreakdown = [
    {
      category: 'Hardware Costs',
      annualOnPremise: '₹3,00,000',
      annualCloud: '₹0',
      saving: '₹3,00,000'
    },
    {
      category: 'IT Staff Costs',
      annualOnPremise: '₹2,00,000',
      annualCloud: '₹50,000',
      saving: '₹1,50,000'
    },
    {
      category: 'License Maintenance',
      annualOnPremise: '₹1,50,000',
      annualCloud: '₹0',
      saving: '₹1,50,000'
    },
    {
      category: 'Backup & Recovery',
      annualOnPremise: '₹75,000',
      annualCloud: '₹0',
      saving: '₹75,000'
    },
    {
      category: 'Energy & Infrastructure',
      annualOnPremise: '₹75,000',
      annualCloud: '₹0',
      saving: '₹75,000'
    },
    {
      category: 'Training & Implementation',
      annualOnPremise: '₹1,50,000',
      annualCloud: '₹25,000',
      saving: '₹1,25,000'
    }
  ];

  const totalSavings = savingsBreakdown.reduce((acc, item) => {
    const savingNum = parseInt(item.saving.replace(/[^0-9]/g, ''));
    return acc + savingNum;
  }, 0);

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Every rupee counts in education. School budgets are tight. College finances are carefully managed. University expenses are scrutinized. In this environment, spending on IT infrastructure and maintenance is a major line item that often goes unquestioned.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">But what if you could save ₹5-10 lakhs every year on IT maintenance?</strong> What if that money could be redirected to scholarships, better facilities, or teacher salaries? For most Indian institutions, this is the difference between thriving and just surviving.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how switching to cloud ERP saves Indian institutions ₹5-10 lakhs annually on IT maintenance.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">₹5-10L</div>
          <div className="text-sm text-slate-500">Annual IT maintenance savings</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">60%</div>
          <div className="text-sm text-slate-500">Average IT cost reduction</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">100%</div>
          <div className="text-sm text-slate-500">No upfront hardware costs</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Where the Savings Come From</h2>

      {/* Cost Areas */}
      {costAreas.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">💸 {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Saves You Money</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Savings Breakdown Table */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 overflow-x-auto">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Annual IT Cost Comparison: On-Premise vs. Cloud ERP</h3>
        <div className="w-full">
          <div className="grid grid-cols-4 gap-2 text-xs sm:text-sm">
            <div className="font-bold text-slate-900 p-2 border-b border-slate-200">Category</div>
            <div className="font-bold text-red-600 p-2 border-b border-slate-200 text-center">On-Premise</div>
            <div className="font-bold text-emerald-600 p-2 border-b border-slate-200 text-center">Cloud ERP</div>
            <div className="font-bold text-indigo-600 p-2 border-b border-slate-200 text-center">Savings</div>
            
            {savingsBreakdown.map((item, i) => (
              <React.Fragment key={i}>
                <div className="p-2 border-b border-slate-100">{item.category}</div>
                <div className="p-2 border-b border-slate-100 text-center text-red-600">{item.annualOnPremise}</div>
                <div className="p-2 border-b border-slate-100 text-center text-emerald-600">{item.annualCloud}</div>
                <div className="p-2 border-b border-slate-100 text-center text-indigo-600 font-bold">{item.saving}</div>
              </React.Fragment>
            ))}
            
            <div className="p-3 border-t-2 border-slate-300 font-bold text-slate-900">Total Annual Savings</div>
            <div className="p-3 border-t-2 border-slate-300 text-center"></div>
            <div className="p-3 border-t-2 border-slate-300 text-center"></div>
            <div className="p-3 border-t-2 border-slate-300 text-center text-indigo-600 font-bold text-base">₹{totalSavings.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* What That Money Means */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What ₹5-10 Lakhs Could Mean for Your Institution</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
            <div className="text-2xl mb-1">🎓</div>
            <h4 className="font-bold text-slate-900 text-sm">Student Scholarships</h4>
            <p className="text-xs text-slate-600">Fund 10-20 scholarships for deserving students</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-2xl mb-1">👨‍🏫</div>
            <h4 className="font-bold text-slate-900 text-sm">Teacher Development</h4>
            <p className="text-xs text-slate-600">Training programs for 20-30 faculty members</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="text-2xl mb-1">🏫</div>
            <h4 className="font-bold text-slate-900 text-sm">Infrastructure Upgrades</h4>
            <p className="text-xs text-slate-600">Improve classrooms, labs, or sports facilities</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">Library & Resources</h4>
            <p className="text-xs text-slate-600">Add new books, journals, and learning materials</p>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Delivers Maximum Cost Savings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">Zero Upfront Cost</h4>
            <p className="text-xs text-slate-600">No hardware purchases, no capital expenses</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Predictable Subscription</h4>
            <p className="text-xs text-slate-600">Affordable monthly or annual payments</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Free to Start</h4>
            <p className="text-xs text-slate-600">Try Truyon without any financial commitment</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔧</div>
            <h4 className="font-bold text-slate-900 text-sm">No Maintenance Cost</h4>
            <p className="text-xs text-slate-600">Updates, backups, and security included</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Scale Without Cost</h4>
            <p className="text-xs text-slate-600">Add users and institutions without hardware upgrades</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">ROI in First Year</h4>
            <p className="text-xs text-slate-600">Most institutions save more than the subscription cost</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Save Money. Invest in What Matters.</h3>
        <p className="text-indigo-800 leading-relaxed">
          Indian institutions spend ₹5-10 lakhs annually on IT maintenance for on-premise systems. That's money that could be going to students, teachers, and facilities—the things that truly matter in education.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Cloud ERP from Truyon eliminates these costs. No hardware. No IT staff. No maintenance fees. No backups. No energy costs. Just a simple, affordable subscription that saves you money from day one.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for cloud ERP.</strong> Save ₹5-10 lakhs annually. Invest in your institution's future.
        </p>
      </div>
    </>
  );
};

export default CloudSavings;